export const commonUserSummarySelect = {
  id: true,
  displayName: true,
  mail: true,
} as const

export const commonThesisIterationSelect = {
  id: true,
  title: true,
  submissionId: true,
  recordId: true,
  type: true,
  hasOral: true,
  organisationId: true,
  additionalSupervisors: true,
  submittedDate: true,
  oralDate: true,
  principalAdvisorId: true,
  statusHistory: {
    select: {
      id: true,
      status: true,
      outcome: true,
      outcomeMessage: true,
    },
  },
  files: {
    select: {
      id: true,
      key: true,
      filename: true,
      submissionDate: true,
      type: true,
      isActive: true,
    },
  },
} as const

export const commonSubmissionBaseSelect = {
  id: true,
  isDowngradedToMPhil: true,
  repeatOralCount: true,
  reviseAndResubmitOfId: true,
  creator: { select: commonUserSummarySelect },
  principalAdvisor: { select: commonUserSummarySelect },
  chairs: {
    where: { isActive: true },
    select: {
      id: true,
      fileId: true,
      graduateSchoolNote: true,
      user: { select: commonUserSummarySelect },
    },
  },
  iterations: {
    select: {
      ...commonThesisIterationSelect,
      intendedSubmissionDate: true,
      abstract: true,
      forCodes: true,
      embargoType: true,
      totalPages: true,
      keywords: true,
      isUqIntellectualProperty: true,
    },
  },
  thesisSubmissionExaminers: {
    select: {
      id: true,
      status: true,
      mail: true,
      fullName: true,
      candidateCOIStatements: true,
      advisorCOIStatements: true,
      user: { select: commonUserSummarySelect },
    },
  },
} as const

export const deanSubmissionBaseSelect = {
  id: true,
  repeatOralCount: true,
  reviseAndResubmitOfId: true,
  iterations: {
    select: { ...commonThesisIterationSelect, intendedSubmissionDate: true },
  },
  creator: { select: commonUserSummarySelect },
  principalAdvisor: { select: commonUserSummarySelect },
  thesisSubmissionExaminers: {
    select: {
      id: true,
      mail: true,
      fullName: true,
      status: true,
      candidateCOIStatements: true,
      advisorCOIStatements: true,
      user: { select: commonUserSummarySelect },
    },
  },
  chairs: {
    where: { isActive: true },
    select: {
      id: true,
      fileId: true,
      graduateSchoolNote: true,
      user: { select: commonUserSummarySelect },
    },
  },
} as const

export const fullSubmissionBaseSelect = {
  id: true,
  isDowngradedToMPhil: true,
  repeatOralCount: true,
  reviseAndResubmitOfId: true,
  creator: { select: commonUserSummarySelect },
  principalAdvisor: { select: commonUserSummarySelect },
  iterations: {
    select: {
      ...commonThesisIterationSelect,
      intendedSubmissionDate: true,
      abstract: true,
      forCodes: true,
      embargoType: true,
      totalPages: true,
      keywords: true,
      isUqIntellectualProperty: true,
    },
  },
  chairs: {
    where: { isActive: true },
    select: {
      id: true,
      fileId: true,
      graduateSchoolNote: true,
      detailedKnowledgeOfThesis: true,
      understandingResearchInBroaderContext: true,
      defendMethodologyAndConclusions: true,
      communicatedResultsAndImpact: true,
      independentContributionToDiscipline: true,
      examinationSummary: true,
      user: { select: commonUserSummarySelect },
    },
  },
  thesisSubmissionExaminers: {
    select: {
      id: true,
      title: true,
      position: true,
      institution: true,
      rationaleForNomination: true,
      contactNumber: true,
      contactAddress: true,
      requirePayment: true,
      bankAccountType: true,
      outcomeMessage: true,
      graduateSchoolNote: true,
      joinedAt: true,
      status: true,
      mail: true,
      fullName: true,
      advisorCOIDetails: true,
      candidateCOIDetails: true,
      examinerCOIDetails: true,
      candidateCOIStatements: true,
      advisorCOIStatements: true,
      examinerCOIStatements: true,
      deanCOIDecision: true,
      user: { select: commonUserSummarySelect },
    },
  },
} as const

export const thesisSubmissionDetailsSelect = {
  id: true,
  uuid: true,
  reviseAndResubmitOfId: true,
  showExaminerReports: true,
  showChairFeedback: true,
  isDowngradedToMPhil: true,
  repeatOralCount: true,
  resubmittedSubmission: { select: { id: true } },
  creator: {
    select: { id: true, mail: true, displayName: true },
  },
  principalAdvisor: {
    select: {
      id: true,
      mail: true,
      displayName: true,
      eduPersonPrincipalName: true,
      organisation: { select: { name: true } },
    },
  },
  iterations: {
    select: {
      id: true,
      abstract: true,
      embargoType: true,
      keywords: true,
      totalPages: true,
      isUqIntellectualProperty: true,
      title: true,
      type: true,
      hasOral: true,
      intendedSubmissionDate: true,
      submittedDate: true,
      oralDate: true,
      chairSummaryReportDueDate: true,
      examinerFeedbackDueDate: true,
      newIteration: { select: { id: true } },
      statusHistory: {
        select: {
          id: true,
          status: true,
          outcome: true,
          outcomeMessage: true,
          previousStatusId: true,
        },
      },
      record: {
        select: {
          id: true,
          uuid: true,
          name: true,
          collaborators: {
            select: {
              isLead: true,
              isOwner: true,
              userId: true,
              user: {
                select: {
                  id: true,
                  displayName: true,
                  eduPersonPrincipalName: true,
                },
              },
            },
          },
        },
      },
      forCodes: {
        select: {
          id: true,
          code: true,
          name: true,
        },
      },
      organisation: {
        select: {
          id: true,
          name: true,
        },
      },
      principalAdvisor: {
        select: { displayName: true },
      },
      additionalSupervisors: {
        select: {
          id: true,
          displayName: true,
        },
      },
      files: {
        select: {
          id: true,
          key: true,
          examinerId: true,
          type: true,
          filename: true,
          submissionDate: true,
          isNew: true,
          isActive: true,
        },
      },
    },
  },
  chairs: {
    where: { isActive: true },
    select: {
      id: true,
      graduateSchoolNote: true,
      fileId: true,

      examinationSummary: true,
      detailedKnowledgeOfThesis: true,
      understandingResearchInBroaderContext: true,
      defendMethodologyAndConclusions: true,
      communicatedResultsAndImpact: true,
      independentContributionToDiscipline: true,
      amendments: true,
      otherAmendmentDetails: true,
      chairOutcomeRecommendation: true,
      canViewSubmission: true,
      summaryReportCompleted: true,
      user: {
        select: {
          id: true,
          displayName: true,
          eduPersonPrincipalName: true,
          graduateSchoolNote: true,
          organisation: { select: { name: true } },
        },
      },
    },
  },
  thesisSubmissionExaminers: {
    select: {
      id: true,
      submissionId: true,
      userId: true,
      title: true,
      fullName: true,
      mail: true,
      position: true,
      institution: true,
      contactNumber: true,
      contactAddress: true,
      requirePayment: true,
      bankAccountType: true,
      graduateSchoolNote: true,
      candidateCOIStatements: true,
      candidateCOIDetails: true,
      advisorCOIStatements: true,
      advisorCOIDetails: true,
      examinerCOIStatements: true,
      examinerCOIDetails: true,
      contributionKnowledge: true,
      engagementWithLiterature: true,
      advancedKnowledge: true,
      researchCoherence: true,
      convincingWriting: true,
      canViewSubmission: true,
      examinationFeedbackCompleted: true,
      thesesAssessed: true,
      candidatesSupervised: true,
      examinerRecommendation: true,
      poorThesisQuality: true,
      otherOralAttendanceArrangements: true,
      oralAttendanceType: true,
      rationaleForNomination: true,
      outcomeMessage: true,
      status: true,
      user: {
        select: {
          id: true,
          displayName: true,
          firstName: true,
          surname: true,
          mail: true,
          graduateSchoolNote: true,
          eduPersonPrincipalName: true,
        },
      },
    },
  },
  thesis: {
    select: {
      espacePid: true,
    },
  },
} as const
